import { alertController, loadingController, modalController, popoverController, toastController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, Host, Prop, State, forceUpdate, h } from '@stencil/core';
import { User } from '../../global/models';
import { AuthService } from '../../global/service/AuthService';

@Component({
  tag: 'concourse-profile',
})
export class ConcourseProfile {
  static async present(heading: string = null, callback: Function = null): Promise<void> {
    const modal = await modalController.create({
      component: 'concourse-profile',
      cssClass: 'concourse-modal modal-height-auto',
      backdropDismiss: false,
      componentProps: { dismissButton: true, heading },
    });

    modal.addEventListener('profileSaved', () => {
      if (callback) callback();
      modal.dismiss();
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    return await modal.present();
  }

  @Prop() dismissButton: boolean;
  @Prop() heading: string;
  @Prop() email: string;
  @Prop() code: string;

  @Element() el: HTMLElement;

  // sometimes this form is used when someone is not logged in.
  @State() emailUpdated: string;
  @State() codeUpdated: string;
  @State() passwordUpdated: string;

  @State() errorMessage: string;
  @State() successMessage: string;
  @State() isUpdating: boolean = false;
  //@State() profileColor: string = this.user.doc.color;

  @Event() profileSaved: EventEmitter;
  @Event() dismiss: EventEmitter;

  private userCopy: User;

  get mustValidate(): boolean {
    return !this.userCopy?.doc.email_verified || !this.userCopy?.doc.has_password;
  }

  async componentWillLoad() {
    const user: User = globalThis.user;
    this.codeUpdated = this.code;
    this.emailUpdated = this.email || user?.doc.email;
    this.userCopy = user ? new User({ ...user.doc }) : null;
  }

  async onRequestCodeClick() {
    const loading = await loadingController.create({ message: 'Requesting new access code' });
    try {
      loading.present();
      await AuthService.requestAccessCode();
      this.errorMessage = null;
      toastController.create({ message: 'New access code sent to your email', duration: 10000 }).then(toast => toast.present());
    } catch (error) {
      this.errorMessage = error.message;
    } finally {
      loading.dismiss();
    }
  }

  async onConfirmButtonClick() {
    console.log('onConfirmButtonClick');
    const validationMessage = this.getValidationError();
    if (validationMessage) {
      this.errorMessage = validationMessage;
      return;
    }

    const loading = await loadingController.create({ message: 'Validating access code' });
    try {
      loading.present();
      this.isUpdating = true;
      const sessionData = await AuthService.validateAccessCode(this.emailUpdated, this.codeUpdated, this.passwordUpdated);
      console.log('mustValidate:', this.mustValidate, globalThis.user);
      this.userCopy = new User({ ...sessionData.user });
      forceUpdate(this);
    } catch (error) {
      console.error('Error confirming access code', error);
      this.errorMessage = error.message;
    } finally {
      loading.dismiss();
      this.isUpdating = false;
    }
  }

  getValidationError(): string {
    const requiredInputs: NodeListOf<HTMLInputElement> = this.el.querySelectorAll('ion-input input[required]');
    requiredInputs.forEach(input => {
      if (!input.value) {
        return 'Please fill in all required fields';
      }
    });

    if (!this.userCopy?.doc.has_password && !this.passwordUpdated) {
      return 'Password is required';
    }

    return null;
  }

  private fileInputRef?: HTMLInputElement;

  onBrowseImageClick = () => {
    this.fileInputRef?.click();
  };

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.userCopy.setProfilePictureFromFile(input.files[0]);
      setTimeout(() => forceUpdate(this), 100);
      forceUpdate(this);
      input.value = ''; // Clear the input value to allow re-uploading the same file
      console.log('Profile picture updated:', this.userCopy.pictureUrl);
    }
  }

  async onSaveClick() {
    this.errorMessage = this.getValidationError();
    if (this.errorMessage) {
      return;
    }

    try {
      this.isUpdating = true;
      await AuthService.updateUser(this.userCopy.doc);
      document.dispatchEvent(new CustomEvent('refreshAppRoot'));
      toastController.create({ message: 'Profile updated', duration: 3000 }).then(toast => toast.present());
      this.profileSaved.emit();
    } catch (error) {
      this.errorMessage = error.message;
      console.error('Error updating user', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async onDeleteAccountClick() {
    const alert = await alertController.create({
      header: 'Confirm Delete Account',
      message: 'Are you sure you want to delete your account? This action cannot be undone.',
      cssClass: 'modal-content',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'color-white',
        },
        {
          text: 'Delete',
          role: 'delete',
          cssClass: 'color-danger',
          handler: async () => {
            this.isUpdating = true;
            const errorMessage = await AuthService.deleteAccount();
            if (errorMessage) {
              this.errorMessage = errorMessage;
              this.isUpdating = false;
            } else {
              await alert.dismiss();
              this.dismiss.emit();
              window.location.hash = '/';
            }
          },
        },
      ],
    });
    await alert.present();
  }

  private avatarStyle = {
    'max-width': '100px',
    'max-height': '100px',
    'float': 'left',
    'margin-right': '16px',
    'border': '1px solid white',
    'text-align': 'center',
    'cursor': 'pointer',
  };

  render() {
    const photo = this.userCopy?.doc.image ? this.userCopy.doc.image : null;
    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={this.mustValidate ? 'Validate Access Code' : this.heading || 'Update your personal information'}
          showDismiss={this.dismissButton}
          onDismiss={() => this.dismiss.emit()}
        />
        <ion-page class="modal-content">
          {this.mustValidate ? (
            <ion-grid class="cc-padding">
              <ion-row>
                <ion-col>
                  <ion-input
                    id="signup-email"
                    label="Email"
                    label-placement="stacked"
                    disabled={!!this.userCopy?.doc.email}
                    value={this.emailUpdated}
                    onIonChange={e => (this.emailUpdated = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col>
                  <span class="cc-margin-bottom" style={{ 'font-size': '0.9em', 'color': 'grey' }}>
                    {!!this.code ? 'Please confirm that the code below matches what you received from Concourse' : 'Enter the code from the email you received from Concourse'}
                  </span>
                  <ion-input
                    id="signup-code"
                    label="Access Code"
                    label-placement="stacked"
                    disabled={!!this.code}
                    value={this.codeUpdated}
                    onIonChange={e => (this.codeUpdated = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
              {!this.userCopy?.doc.has_password && (
                <ion-row>
                  <ion-col>
                    <span class="cc-margin-bottom" style={{ 'font-size': '0.9em', 'color': 'grey' }}>
                      Create a new password that is at least 6 characters long. <br />A strong password has a combination of letters, digits and punctuation marks.
                    </span>
                    <ion-input
                      id="signup-password"
                      type="password"
                      label="New Password"
                      label-placement="stacked"
                      onIonChange={e => (this.passwordUpdated = e.target.value)}
                    ></ion-input>
                  </ion-col>
                </ion-row>
              )}
            </ion-grid>
          ) : (
            <ion-grid class="cc-padding">
              <ion-row lines="none">
                <ion-col onClick={() => this.onBrowseImageClick()}>
                  <ion-avatar style={this.avatarStyle}>
                    {!!photo ? (
                      <img src={`data:${photo.mimeType};base64,${photo.data}`} width="100px" height="100px" alt="profile picture" />
                    ) : (
                      <ion-icon name="camera-outline" style={{ 'margin-top': '15px' }} size="large"></ion-icon>
                    )}
                  </ion-avatar>
                  <h2 class="ion-no-margin">Photo</h2>
                  <p>Your photo lets people recognize you while working together.</p>
                  <input type="file" accept="image/*" style={{ display: 'none' }} ref={el => (this.fileInputRef = el)} onChange={e => this.onFileInputChange(e)} />
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-first-name"
                    label="First Name"
                    label-placement="stacked"
                    required
                    type="text"
                    value={this.userCopy.doc.first_name}
                    onIonChange={e => (this.userCopy.doc.first_name = e.target.value)}
                  />
                </ion-col>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-last-name"
                    label="Last Name"
                    label-placement="stacked"
                    type="text"
                    value={this.userCopy.doc.last_name}
                    onIonChange={e => (this.userCopy.doc.last_name = e.target.value)}
                  />
                </ion-col>
              </ion-row>
              <ion-row lines="none">
                <ion-col size="12">
                  <ion-input label="Work Email" label-placement="stacked" disabled type="email" value={this.userCopy.doc.email}></ion-input>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-company"
                    label="Company (optional)"
                    label-placement="stacked"
                    type="text"
                    value={this.userCopy.doc.company}
                    onIonChange={e => (this.userCopy.doc.company = e.target.value)}
                  ></ion-input>
                </ion-col>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-company-role"
                    label="Company Role (optional)"
                    label-placement="stacked"
                    type="text"
                    value={this.userCopy.doc.company_role}
                    onIonChange={e => (this.userCopy.doc.company_role = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-phone"
                    label="Phone (optional)"
                    label-placement="stacked"
                    type="tel"
                    value={this.userCopy.doc.phone}
                    onIonChange={e => (this.userCopy.doc.phone = e.target.value)}
                  ></ion-input>
                </ion-col>
                <ion-col size="6" sizeMd="6">
                  <ion-input
                    id="profile-phone-mobile"
                    label="Mobile Phone (optional)"
                    label-placement="stacked"
                    type="tel"
                    value={this.userCopy.doc.phone_mobile}
                    onIonChange={e => (this.userCopy.doc.phone_mobile = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col>
                  <ion-fab-button id="profile-color" style={{ '--background': this.userCopy.doc.color || '#FF5733', 'float': 'left', 'margin-right': '16px' }}></ion-fab-button>
                  <h2>Color</h2>
                  <p>Your color helps you stand out from other people.</p>
                  <ion-popover trigger="profile-color" trigger-action="click" side="right" alignment="end">
                    <ion-row class="ion-padding">
                      {User.COLORS.map(color => {
                        return (
                          <ion-fab-button
                            class="color-option"
                            style={{ '--background': color }}
                            onClick={() => {
                              this.userCopy.doc.color = color;
                              popoverController.dismiss();
                              forceUpdate(this);
                            }}
                          />
                        );
                      })}
                    </ion-row>
                  </ion-popover>
                </ion-col>
              </ion-row>
            </ion-grid>
          )}
        </ion-page>
        <ion-footer>
          {this.errorMessage && <ion-text color="danger">{this.errorMessage}</ion-text>}
          {this.successMessage && <ion-text color="success">{this.successMessage}</ion-text>}
          {this.mustValidate ? (
            <ion-toolbar>
              <ion-button slot="start" fill="outline" onClick={() => this.onRequestCodeClick()}>
                Request New Code
              </ion-button>
              <ion-button slot="end" id="signup-confirm" disabled={this.isUpdating} onClick={() => this.onConfirmButtonClick()}>
                Confirm
              </ion-button>
            </ion-toolbar>
          ) : (
            <ion-toolbar>
              <ion-button id="delete-account" color="danger" fill="clear" disabled={this.isUpdating} slot="start" onClick={() => this.onDeleteAccountClick()}>
                <ion-icon color="danger" slot="start" name="trash-outline"></ion-icon>
                Delete Account
              </ion-button>
              <ion-button id="profile-save" disabled={this.isUpdating} slot="end" shape="round" onClick={() => this.onSaveClick()}>
                Save Profile
              </ion-button>
            </ion-toolbar>
          )}
        </ion-footer>
      </Host>
    );
  }
}
